import { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import { useGameSocket } from '../hooks/useGameSocket';
import { useGameHistory } from '../hooks/useGameHistory';
import { API_ENDPOINTS, GAME_DEFAULTS } from '../config/constants';
import { spawnBotsRequest } from '../services/api';

export const BotSettingsContext = createContext();
export const BotGameContext = createContext();
export const BotActionsContext = createContext();

/**
 * Hook to access bot setup and configuration state.
 */
export const useBotSettings = () => {
    const context = useContext(BotSettingsContext);
    if (!context) throw new Error("useBotSettings must be used within a BotProvider");
    return context;
};

/**
 * Hook to access real-time game and socket state.
 */
export const useBotGame = () => {
    const context = useContext(BotGameContext);
    if (!context) throw new Error("useBotGame must be used within a BotProvider");
    return context;
};

/**
 * Hook to access bot control actions (launch, answer, etc).
 */
export const useBotActions = () => {
    const context = useContext(BotActionsContext);
    if (!context) throw new Error("useBotActions must be used within a BotProvider");
    return context;
};

export const BotProvider = ({ children }) => {
    const [form, setForm] = useState({ pin: '', amount: GAME_DEFAULTS.INITIAL_BOT_AMOUNT, name: '', teamMode: false });
    const [setupStep, setSetupStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const { addToHistory } = useGameHistory();

    /**
     * Use the custom useGameSocket hook to handle all socket.io logic and game state.
     */
    const {
        gameState, botCount, timeline, timeLeft, totalTime, answerMode, setAnswerMode,
        podium, questionNumber, socketRef, connectSocket, disconnectBots, sendAnswer
    } = useGameSocket({ setSetupStep, setLoading, setErrors });

    /**
     * Sends a request to the backend to initialize and join bots into a game.
     */
    const launchBots = useCallback(async () => {
        setLoading(true);
        setErrors({});

        if (!socketRef.current || !socketRef.current.connected) {
            connectSocket();
            let attempts = 0;
            while ((!socketRef.current || !socketRef.current.connected) && attempts < 20) {
                await new Promise(r => setTimeout(r, 100));
                attempts++;
            }

            if (!socketRef.current?.connected) {
                setErrors({ pin: "Could not connect to server. Check if backend is running." });
                setLoading(false);
                return;
            }
        }

        try {
            await spawnBotsRequest({
                pin: form.pin,
                amount: form.amount,
                name: form.name,
                clientId: socketRef.current.id,
                teamMode: form.teamMode
            });

            // Save to history on successful launch
            addToHistory({
                pin: form.pin,
                amount: form.amount,
                name: form.name,
                teamMode: form.teamMode
            });
        } catch (error) {
            const desc = error.message.toLowerCase();

            if (desc.includes("pin")) {
                setSetupStep(0);
                setErrors({ pin: error.message });
            } else if (desc.includes("name")) {
                setSetupStep(1);
                setErrors({ name: error.message });
            } else if (desc.includes("network error") || desc.includes("fetch")) {
                const message = `Network Error: Is backend running on ${API_ENDPOINTS.INIT}?`;
                setErrors({ pin: message });
                notifications.show({ title: 'Connection Error', message, color: 'red' });
            } else {
                notifications.show({
                    title: 'Error',
                    message: error.message || "An unknown error occurred",
                    color: 'red'
                });
            }
            setLoading(false);
        }
    }, [form, socketRef, connectSocket, addToHistory]);

    // Memoize context values to prevent unnecessary re-renders
    const settingsValue = useMemo(() => ({
        form, setupStep, loading, errors
    }), [form, setupStep, loading, errors]);

    const gameValue = useMemo(() => ({
        gameState, botCount, timeline, timeLeft, totalTime, answerMode, podium, questionNumber
    }), [gameState, botCount, timeline, timeLeft, totalTime, answerMode, podium, questionNumber]);

    const actionsValue = useMemo(() => ({
        setForm, setSetupStep, setErrors, setAnswerMode,
        connectSocket, launchBots, disconnectBots, sendAnswer
    }), [setForm, setSetupStep, setErrors, setAnswerMode, connectSocket, launchBots, disconnectBots, sendAnswer]);

    return (
        <BotActionsContext.Provider value={actionsValue}>
            <BotSettingsContext.Provider value={settingsValue}>
                <BotGameContext.Provider value={gameValue}>
                    {children}
                </BotGameContext.Provider>
            </BotSettingsContext.Provider>
        </BotActionsContext.Provider>
    );
};